import { useState, useRef, useEffect } from 'react'
import debounce from 'lodash/debounce'

const DEFAULT_DEBOUNCE_TIME = 1000

interface DebouncedSearchArgs<T> {
  value: T
  onChange: (state: T) => unknown
  debounceTime?: number
}

export function useDebounced<T>({
  value: initialValue,
  onChange,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
}: DebouncedSearchArgs<T>) {
  const [value, setValue] = useState(initialValue)
  // useRef to always use the most updated function on debounce
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // useRef to avoid redefinition on every render
  const debouncedChangeHandler = useRef(
    debounce((search: T) => onChangeRef.current(search), debounceTime)
  )

  function handleSearchChange(value: T) {
    setValue(value)
    debouncedChangeHandler.current(value)
  }

  function handleSearchSubmit() {
    onChangeRef.current(value)
    debouncedChangeHandler.current.cancel()
  }

  function handleSearchClear() {
    onChangeRef.current(initialValue)

    setValue(initialValue)
    debouncedChangeHandler.current.cancel()
  }

  return {
    value,
    handleSearchClear,
    handleSearchSubmit,
    handleSearchChange,
  }
}
